import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import cn from 'classnames';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FiCheckCircle,
  FiChevronDown,
  FiPlay,
  FiDownload,
  FiList,
  FiLoader,
  FiMoreVertical,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash,
} from 'react-icons/fi';

import { Button } from '../common/Button';
import ConfirmModal from '../common/ConfirmModal';
import {
  appointmentService,
  canStartExamNow,
  formatAppointmentClock,
  getApiErrorMessage,
  isAppointmentPassed,
  isInProgressPastEnd,
  canModifyAppointment,
  type AppointmentRead,
} from '../../../helpers/api/appointment/appointment';
import { appointmentKeys } from '../../../helpers/api/appointment/appointment-query-keys';
import { useMe } from '../../../helpers/api/users/users';
import { usePermissions } from '../../../helpers/acl/permissions';
import SegmentedControl from '../Scheduling/SegmentedControl';
import {
  getAppointmentStatusVisual,
  getPriorityVisual,
} from '../Scheduling/schedulingStatus';
import { useStartAppointmentExam } from '../Scheduling/useStartAppointmentExam';
import { apiClient } from '../../../helpers/apiHelper';
import type { EnrichedWorklistItem } from '../../../helpers/api/dicom/dicom';
import { facilityProcedureService } from '../../../helpers/api/facility-procedure/facility-procedure';
import {
  getCurrentFacilityId,
  useCurrentFacility,
} from '../../../helpers/acl/facility';
import {
  formatMwlBirthDate,
  pendingRowFromMwl,
  type PendingMwlRow,
} from './mwlMerge';

const WORKLIST_MODE_KEY = 'scheduling.worklistMode';
const MWL_SERVER_KEY = 'scheduling.mwlServerId';

/** Shared column template — header, MWL rows, and local rows must match. */
const WORKLIST_GRID =
  'grid min-w-[1280px] grid-cols-[40px_68px_minmax(160px,1.25fr)_minmax(130px,1.05fr)_minmax(110px,0.9fr)_minmax(110px,0.9fr)_minmax(100px,0.8fr)_118px_148px] items-center gap-x-3 px-4';

function mwlServerLabel(source: any): string {
  const label = String(source?.label || '').trim();
  if (label) return label;
  const ae = String(source?.aeTitle || '').trim();
  const host = String(source?.hostname || '').trim();
  if (ae && host) return `${ae}@${host}:${source?.port ?? ''}`;
  if (ae) return ae;
  return source?.id != null ? `Destination ${source.id}` : 'Worklist server';
}


function isUsableMwlServer(source: any): boolean {
  if (source?.id == null) return false;
  if (!String(source?.aeTitle || '').trim()) return false;
  if (!String(source?.hostname || '').trim()) return false;
  return !!source?.port;
}

function pickDefaultMwlServerId(sources: any[]): number | null {
  const usable = sources.filter(isUsableMwlServer);
  const preferred = usable.find((s) => s.supportsWorklist) || usable[0];
  return preferred?.id != null ? Number(preferred.id) : null;
}

type WorklistMode = 'facility' | 'mine';
type QueueFilter = 'all' | 'scheduled' | 'in_progress' | 'finished' | 'mwl';

type Props = {
  date: string;
  onOpenBooking?: () => void;
  onSelectAppointment?: (app: AppointmentRead) => void;
  /**
   * Same staff scope as the calendar header filter.
   * Used when Facility Worklist is selected; My Worklist always uses the current user.
   */
  performingProviderId?: string;
  embedded?: boolean;
};

export default function WorklistPanel({
  date,
  onOpenBooking,
  onSelectAppointment,
  performingProviderId,
  embedded = false,
}: Props) {
  const { t } = useTranslation('Scheduling');
  const queryClient = useQueryClient();
  const { data: user } = useMe();
  const userId = String(user?.id || user?.data?.id || '');
  const { can } = usePermissions();
  const canWrite = can('appointment.write');
  const { scope } = useCurrentFacility();
  const selectedFacilityId =
    scope?.kind === 'facility' ? scope.id : getCurrentFacilityId();
  const {
    startExam,
    startingExamId,
    examFlowModals,
  } = useStartAppointmentExam();

  const [mode, setMode] = useState<WorklistMode>(() => {
    try {
      const saved = localStorage.getItem(WORKLIST_MODE_KEY);
      return saved === 'mine' ? 'mine' : 'facility';
    } catch {
      return 'facility';
    }
  });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [queueFilter, setQueueFilter] = useState<QueueFilter>('all');
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);
  const [mwlServerId, setMwlServerId] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem(MWL_SERVER_KEY);
      return saved ? Number(saved) : null;
    } catch {
      return null;
    }
  });
  const [selectedPending, setSelectedPending] = useState<Record<string, PendingMwlRow>>(
    {}
  );
  /** Hide MWL rows immediately while/after create so the list doesn't flash SYNCING. */
  const [dismissedMwlKeys, setDismissedMwlKeys] = useState<Set<string>>(
    () => new Set()
  );

  const [includeOtherFacilities, setIncludeOtherFacilities] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Align with calendar staff filter:
  // - My Worklist → current user
  // - Facility Worklist → header staff selection (or all staff when unset)
  const providerFilter = useMemo(() => {
    if (mode === 'mine') return userId || undefined;
    return performingProviderId || undefined;
  }, [mode, userId, performingProviderId]);

  const filters = useMemo(
    () => ({
      date_from: date,
      date_to: date,
      // Keep finished visits visible (calendar already shows them).
      status: ['scheduled', 'in_progress', 'completed'],
      performing_provider_id: providerFilter,
      search: debouncedSearch || undefined,
    }),
    [date, providerFilter, debouncedSearch]
  );

  const { data: rows = [], isLoading } = useQuery({
    queryKey: appointmentKeys.list(filters),
    queryFn: () => appointmentService.list(filters),
    enabled: !!date,
  });

  const { data: servers = [], isFetched: serversFetched } = useQuery({
    queryKey: ['mwl-worklist-sources'],
    queryFn: async () => {
      const res: any = await apiClient.get('/mwl/remote/destinations', {
        capability: 'worklist',
      });
      const body = res?.data?.data || res?.data || res;
      const list = body?.destinations || body?.data?.destinations || [];
      return Array.isArray(list) ? list : [];
    },
  });

  const usableServers = useMemo(
    () => servers.filter(isUsableMwlServer),
    [servers]
  );

  const selectedServerReady = useMemo(
    () =>
      mwlServerId != null &&
      usableServers.some((s) => Number(s.id) === Number(mwlServerId)),
    [usableServers, mwlServerId]
  );

  // Wait for /servers, then keep a valid selection or auto-pick the first usable MWL host.
  // Clears stale localStorage IDs that no longer exist / lack host+port+AE.
  useEffect(() => {
    if (!serversFetched) return;
    if (selectedServerReady) return;
    const nextId = pickDefaultMwlServerId(servers);
    setMwlServerId((prev) => (prev === nextId ? prev : nextId));
  }, [serversFetched, servers, selectedServerReady]);

  useEffect(() => {
    if (mwlServerId == null) {
      try {
        localStorage.removeItem(MWL_SERVER_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      localStorage.setItem(MWL_SERVER_KEY, String(mwlServerId));
    } catch {
      /* ignore */
    }
  }, [mwlServerId]);

  const {
    data: mwlResults = [],
    isFetching: mwlLoading,
    refetch: refetchMwl,
  } = useQuery({
    // Scope must be in the key: facility filter uses belongs_to_selected_facility
    // from enrichment, which depends on X-Facility-Id.
    queryKey: [
      'scheduling-mwl',
      mwlServerId,
      date,
      selectedFacilityId || 'org',
    ],
    queryFn: async () => {
      const res: any = await apiClient.post(`/mwl/remote/${mwlServerId}/worklist`, {
        startDateFrom: date,
        startDateTo: date,
      });
      const body = res?.data?.data || res?.data || res;
      // Backend returns 200 with success:false for some failures; treat as error.
      if (res?.data?.status === 'fail' || body?.success === false) {
        throw new Error(
          res?.data?.message || body?.message || 'MWL query failed'
        );
      }
      const results = body?.results || [];
      return Array.isArray(results) ? (results as EnrichedWorklistItem[]) : [];
    },
    // Do not hit DICOM until servers are loaded and the selected id is usable.
    enabled: serversFetched && selectedServerReady && !!mwlServerId && !!date,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Auto-add any new MWL procedure descriptions into the facility catalog.
  // Only for rows that belong to the currently selected facility (avoid seeding
  // Aghbala catalog from ROSH_MFM_PARK rows).
  useEffect(() => {
    if (!canWrite || !mwlResults.length) return;
    const items = mwlResults
      .filter((row) => row.resolution?.belongs_to_selected_facility === true)
      .map((row) => {
        const mwl = row.mwl || {};
        const sps = Array.isArray(mwl.ScheduledProcedureStepSequence)
          ? mwl.ScheduledProcedureStepSequence[0]
          : null;
        const description =
          (mwl.RequestedProcedureDescription || '').trim() ||
          (sps?.ScheduledProcedureStepDescription || '').trim();
        if (!description) return null;
        return {
          description,
          code: mwl.RequestedProcedureID || null,
        };
      })
      .filter(Boolean) as Array<{ description: string; code?: string | null }>;
    if (!items.length) return;

    let cancelled = false;
    (async () => {
      try {
        const result = await facilityProcedureService.ensure(items);
        if (!cancelled && result.created > 0) {
          queryClient.invalidateQueries({ queryKey: ['facility-procedures'] });
        }
      } catch {
        // Non-blocking: import still upserts procedures.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canWrite, mwlResults, queryClient]);

  const pendingMwlRows = useMemo(() => {
    const localAccessions = new Set(
      rows
        .map((r) => (r.accession_number || '').trim())
        .filter(Boolean)
    );
    // Facility scope → only that facility's MWL. Org scope → all facilities.
    const facilityScoped = Boolean(selectedFacilityId);
    return mwlResults
      .filter((item) => {
        if (item.resolution?.appointment_exists) return false;
        const acc = (item.mwl?.AccessionNumber || '').trim();
        if (acc && localAccessions.has(acc)) return false;
        if (facilityScoped && !includeOtherFacilities) {
          // Prefer enrichment flag; also accept direct facility_id match.
          if (item.resolution?.belongs_to_selected_facility === true) {
            return true;
          }
          const resolvedId = item.resolution?.facility_id || null;
          return Boolean(
            resolvedId && selectedFacilityId && resolvedId === selectedFacilityId
          );
        }
        return true;
      })
      .map(pendingRowFromMwl)
      .filter((row) => !dismissedMwlKeys.has(row.key))
      .filter((row) => {
        if (!debouncedSearch) return true;
        const q = debouncedSearch.toLowerCase();
        return (
          row.patientName.toLowerCase().includes(q) ||
          row.patientId.toLowerCase().includes(q) ||
          (row.accession || '').toLowerCase().includes(q) ||
          row.procedure.toLowerCase().includes(q) ||
          (row.facilityName || '').toLowerCase().includes(q) ||
          (row.location || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')));
  }, [
    mwlResults,
    rows,
    debouncedSearch,
    dismissedMwlKeys,
    selectedFacilityId,
    includeOtherFacilities,
  ]);

  const otherFacilityMwlCount = useMemo(() => {
    if (!selectedFacilityId) return 0;
    return mwlResults.filter((item) => {
      if (item.resolution?.appointment_exists) return false;
      if (item.resolution?.belongs_to_selected_facility === true) return false;
      const resolvedId = item.resolution?.facility_id || null;
      return !(resolvedId && resolvedId === selectedFacilityId);
    }).length;
  }, [mwlResults, selectedFacilityId]);

  const invalidateLocal = () => {
    queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
    queryClient.invalidateQueries({ queryKey: appointmentKeys.agendas() });
  };

  const invalidate = () => {
    invalidateLocal();
    queryClient.invalidateQueries({ queryKey: ['scheduling-mwl'] });
  };

  type ImportPayload = {
    items: EnrichedWorklistItem[];
    keys: string[];
  };

  const importMutation = useMutation({
    mutationFn: async ({ items }: ImportPayload) => {
      if (!mwlServerId) throw new Error('Select an MWL server first');
      return appointmentService.importMwl({
        destination_ae_id: mwlServerId,
        items: items.map((row) => ({ ...(row.mwl || {}) })),
      });
    },
    onMutate: ({ keys }) => {
      setSelectedPending((prev) => {
        const next = { ...prev };
        keys.forEach((k) => {
          delete next[k];
        });
        return next;
      });
    },
    onSuccess: (res: any, vars) => {
      const results = res?.results || res?.data?.results || [];
      const created = results.filter((r: any) => r.action === 'created').length;
      const skipped = results.filter((r: any) => r.action === 'skipped').length;
      const errorRows = results.filter((r: any) => r.action === 'error');
      const errors = errorRows.length;
      if (errors > 0 && created === 0 && skipped === 0) {
        const detail = errorRows
          .map((r: any) => r.detail)
          .filter(Boolean)
          .slice(0, 3)
          .join('; ');
        message.error(
          detail ||
            t('worklist.importResult', {
              defaultValue: `Created ${created}, skipped ${skipped}, errors ${errors}`,
              created,
              skipped,
              errors,
            })
        );
      } else {
        message.success(
          t('worklist.importResult', {
            defaultValue: `Created ${created}, skipped ${skipped}, errors ${errors}`,
            created,
            skipped,
            errors,
          })
        );
        if (errors > 0) {
          const detail = errorRows
            .map((r: any) => `${r.accession_number || '?'}: ${r.detail || 'error'}`)
            .slice(0, 3)
            .join('; ');
          if (detail) message.warning(detail);
        }
        // Drop successfully handled MWL rows without refetching the whole MWL query
        setDismissedMwlKeys((prev) => {
          const next = new Set(prev);
          vars.keys.forEach((k) => next.add(k));
          return next;
        });
      }
      // Refresh local appointments only — avoid full MWL refetch / SYNCING overlay
      invalidateLocal();
      queryClient.invalidateQueries({ queryKey: ['facility-procedures'] });
    },
    onError: (err: unknown) => {
      message.error(getApiErrorMessage(err, t('messages.importError', { defaultValue: 'Import failed' })));
    },
  });
  const importingKeys = useMemo(() => {
    if (!importMutation.isPending || !importMutation.variables?.keys) {
      return new Set<string>();
    }
    return new Set(importMutation.variables.keys);
  }, [importMutation.isPending, importMutation.variables]);

  const runImport = (rowsToImport: PendingMwlRow[]) => {
    if (!rowsToImport.length) return;
    importMutation.mutate({
      items: rowsToImport.map((r) => r.mwlItem),
      keys: rowsToImport.map((r) => r.key),
    });
  };
  useEffect(() => {
    setDismissedMwlKeys(new Set());
    setSelectedPending({});
  }, [date, mwlServerId]);

  const autoCompletedRef = useRef<Set<string>>(new Set());

  // Persist overdue in-progress visits as completed once their slot ends.
  useEffect(() => {
    if (!canWrite || !rows.length) return;

    const overdue = rows.filter(
      (app) =>
        isInProgressPastEnd(app) &&
        !autoCompletedRef.current.has(String(app.id))
    );
    if (!overdue.length) return;

    let cancelled = false;
    (async () => {
      let completedAny = false;
      for (const app of overdue) {
        const id = String(app.id);
        autoCompletedRef.current.add(id);
        try {
          await appointmentService.complete(id);
          completedAny = true;
        } catch {
          autoCompletedRef.current.delete(id);
        }
        if (cancelled) return;
      }
      if (completedAny && !cancelled) {
        invalidate();
      }
    })();

    return () => {
      cancelled = true;
    };
    // intentionally depend on row ids + end times, not invalidate identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canWrite, rows]);

  const actionMutation = useMutation({
    mutationFn: async ({
      id,
      action,
    }: {
      id: string;
      action: 'complete' | 'cancel';
    }) => {
      if (action === 'complete') return appointmentService.complete(id);
      return appointmentService.cancel(id);
    },
    onMutate: ({ id }) => setPendingActionId(id),
    onSuccess: (_data, variables) => {
      message.success(
        variables.action === 'cancel'
          ? t('messages.cancelSuccess')
          : t('messages.actionSuccess')
      );
      invalidate();
    },
    onError: (err: unknown) => {
      message.error(getApiErrorMessage(err, t('messages.cancelError')));
    },
    onSettled: () => setPendingActionId(null),
  });

  const confirmCancel = (id: string) => {
    setCancelTargetId(id);
  };

  const handleConfirmCancel = () => {
    if (!cancelTargetId) return;
    const id = cancelTargetId;
    setCancelTargetId(null);
    actionMutation.mutate({ id, action: 'cancel' });
  };

  const setWorklistMode = (next: WorklistMode) => {
    setMode(next);
    try {
      localStorage.setItem(WORKLIST_MODE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const grouped = useMemo(() => {
    const normalize = (r: AppointmentRead) =>
      String(r.status).toLowerCase().replace(/^.*\./, '');

    const inProgress = rows.filter(
      (r) => normalize(r) === 'in_progress' && !isInProgressPastEnd(r)
    );
    const finished = rows.filter((r) => {
      const st = normalize(r);
      return (
        st === 'completed' ||
        st === 'finished' ||
        (st === 'in_progress' && isInProgressPastEnd(r))
      );
    });
    const scheduled = rows.filter((r) => {
      const st = normalize(r);
      if (st === 'in_progress' && !isInProgressPastEnd(r)) return false;
      if (
        st === 'completed' ||
        st === 'finished' ||
        (st === 'in_progress' && isInProgressPastEnd(r))
      ) {
        return false;
      }
      return true;
    });
    return { inProgress, scheduled, finished };
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (queueFilter === 'in_progress') return grouped.inProgress;
    if (queueFilter === 'scheduled') return grouped.scheduled;
    if (queueFilter === 'finished') return grouped.finished;
    if (queueFilter === 'mwl') return [];
    return rows;
  }, [grouped, queueFilter, rows]);

  const showPendingMwl = queueFilter === 'all' || queueFilter === 'mwl';

  const togglePending = (row: PendingMwlRow) => {
    setSelectedPending((prev) => {
      const next = { ...prev };
      if (next[row.key]) delete next[row.key];
      else next[row.key] = row;
      return next;
    });
  };

  const allPendingSelected =
    pendingMwlRows.length > 0 &&
    pendingMwlRows.every((r) => selectedPending[r.key]);

  const toggleSelectAllPending = () => {
    if (allPendingSelected) {
      setSelectedPending({});
      return;
    }
    const next: Record<string, PendingMwlRow> = {};
    pendingMwlRows.forEach((r) => {
      next[r.key] = r;
    });
    setSelectedPending(next);
  };

  const renderPendingRow = (row: PendingMwlRow) => {
    const selected = Boolean(selectedPending[row.key]);
    const rowCreating = importingKeys.has(row.key);
    const importBusy = importMutation.isPending;
    const station =
      row.stationName ||
      row.stationAe ||
      (row.modality ? String(row.modality) : '') ||
      '—';

    return (
      <div
        key={row.key}
        className={cn(
          WORKLIST_GRID,
          'border-b border-amber-100/80 bg-amber-50/50 py-2.5 dark:border-amber-900/30 dark:bg-amber-950/25',
          'border-l-[3px] border-l-amber-400 dark:border-l-amber-500',
          rowCreating && 'opacity-70'
        )}
      >
        <div className="flex justify-center">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            checked={selected}
            disabled={importBusy}
            onChange={() => togglePending(row)}
            aria-label={t('worklist.selectMwl', { defaultValue: 'Select MWL row' })}
          />
        </div>
        <div className="min-w-0">
          <span className="block text-sm font-semibold tabular-nums text-slate-900 dark:text-gray-100">
            {row.startTime ? formatAppointmentClock(row.startTime) : '—'}
          </span>
          <span className="mt-0.5 inline-block rounded bg-amber-100 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
            MWL
          </span>
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
            {row.patientName}
          </p>
          <p className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-gray-400">
            {row.patientId ? `MRN ${row.patientId}` : '—'}
            {row.sex ? ` · ${row.sex}` : ''}
            {row.birthDate ? ` · ${formatMwlBirthDate(row.birthDate)}` : ''}
          </p>
          {(row.phone || row.email) && (
            <p className="mt-0.5 truncate text-[11px] text-slate-400">
              {[row.phone, row.email].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-800 dark:text-gray-200">
            {row.procedure || '—'}
          </p>
          <p className="mt-0.5 truncate font-mono text-[11px] text-slate-400">
            {row.accession || '—'}
            {row.procedureId ? ` · ${row.procedureId}` : ''}
          </p>
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm text-slate-700 dark:text-gray-200">
            {row.requestingPhysician || '—'}
          </p>
          {row.requestingPhysician ? (
            <p className="mt-0.5 truncate text-[11px] text-slate-400">
              {t('worklist.orderingProvider', { defaultValue: 'Ordering' })}
            </p>
          ) : row.phone ? (
            <p className="mt-0.5 truncate text-[11px] text-slate-400">{row.phone}</p>
          ) : null}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm text-slate-700 dark:text-gray-200">{station}</p>
          {row.stationAe && row.stationName ? (
            <p className="mt-0.5 truncate font-mono text-[11px] text-slate-400">
              {row.stationAe}
            </p>
          ) : row.modality && row.stationName ? (
            <p className="mt-0.5 truncate text-[11px] text-slate-400">{row.modality}</p>
          ) : null}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm text-slate-700 dark:text-gray-200">
            {row.facilityName || row.location || '—'}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-1">
            {row.location && row.facilityName ? (
              <span className="truncate font-mono text-[11px] text-slate-400">
                {row.location}
              </span>
            ) : null}
            {row.belongsToSelectedFacility === false ? (
              <span className="inline-flex rounded bg-rose-100 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-rose-800 dark:bg-rose-900/40 dark:text-rose-200">
                {t('worklist.foreignFacility', { defaultValue: 'Other facility' })}
              </span>
            ) : null}
            {row.facilityCreated ? (
              <span className="inline-flex rounded bg-sky-100 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                {t('worklist.newFacility', { defaultValue: 'New facility' })}
              </span>
            ) : null}
          </div>
        </div>
        <div>
          <span
            className={cn(
              'inline-flex rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              rowCreating
                ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
            )}
          >
            {rowCreating
              ? t('worklist.creating', { defaultValue: 'Creating…' })
              : t('worklist.notInDb', { defaultValue: 'Not in DB' })}
          </span>
        </div>
        {canWrite ? (
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              className="h-8 min-w-[6.75rem] gap-1"
              disabled={rowCreating}
              aria-busy={rowCreating}
              onClick={() => {
                if (importBusy && !rowCreating) {
                  message.info(
                    t('worklist.waitCreate', {
                      defaultValue: 'Wait for the current create to finish',
                    })
                  );
                  return;
                }
                runImport([row]);
              }}
            >
              {rowCreating ? (
                <FiLoader size={14} className="animate-spin" />
              ) : (
                <FiPlus size={14} />
              )}
              {rowCreating
                ? t('worklist.creating', { defaultValue: 'Creating…' })
                : t('worklist.create', { defaultValue: 'Create' })}
            </Button>
          </div>
        ) : (
          <div />
        )}
      </div>
    );
  };

  const renderRow = (app: AppointmentRead) => {
    const status = getAppointmentStatusVisual(app, t);
    const prio = getPriorityVisual(app.priority, t);
    const st = String(app.status).toLowerCase().replace(/^.*\./, '');
    const startAllowed = canStartExamNow(app);
    const passed = isAppointmentPassed(app);
    const assignee =
      [app.assignee_first_name, app.assignee_last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || null;
    const orderingProvider =
      [app.ordering_provider_first_name, app.ordering_provider_last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || null;
    const providerName =
      assignee ||
      orderingProvider ||
      (app.doctor_id
        ? null
        : t('worklist.unassigned', { defaultValue: 'Unassigned' }));
    const providerIsOrderingOnly = !assignee && Boolean(orderingProvider);
    const serviceLabel = (app.reason || '').trim() || '—';
    const station =
      (app.scheduled_station_name || '').trim() ||
      (app.scheduled_station_ae_title || '').trim() ||
      (app.modality || '').trim() ||
      '—';
    const location =
      (app.scheduled_procedure_step_location || '').trim() || '—';
    const busy =
      actionMutation.isPending && pendingActionId === app.id
        ? true
        : startingExamId === String(app.id);
    const canCancel = st === 'scheduled' || st === 'confirmed';
    const pastEndInProgress = isInProgressPastEnd(app);
    const showResumeActions = st === 'in_progress' && !pastEndInProgress;
    const isTerminal =
      st === 'completed' ||
      st === 'finished' ||
      st === 'cancelled' ||
      st === 'no_show' ||
      pastEndInProgress;
    // Only show Start Exam when it can actually be used (never a disabled stub).
    const showStartExam =
      !showResumeActions &&
      !isTerminal &&
      !passed &&
      startAllowed &&
      (st === 'scheduled' || st === 'confirmed');
    const canModify = canModifyAppointment(app);

    return (
      <div
        key={app.id}
        className={cn(
          WORKLIST_GRID,
          'border-b border-slate-100 py-2.5 transition-colors hover:bg-slate-50/80 dark:border-gray-700/80 dark:hover:bg-blue-900/10',
          'border-l-[3px] border-l-transparent'
        )}
      >
        <div />
        <div className="min-w-0">
          <span className="block text-sm font-semibold tabular-nums text-slate-900 dark:text-gray-100">
            {formatAppointmentClock(app.start_time)}
          </span>
          <span className="mt-0.5 block text-[11px] text-slate-400">
            {app.duration_minutes || 30} min
          </span>
        </div>

        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-white">
            {app.patient_name} {app.patient_last_name}
          </p>
          <p className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-gray-400">
            {app.patient_mrn ? `MRN ${app.patient_mrn}` : '—'}
          </p>
          {(app.patient_phone_number || app.patient_email) && (
            <p className="mt-0.5 truncate text-[11px] text-slate-400">
              {[app.patient_phone_number, app.patient_email]
                .filter(Boolean)
                .join(' · ')}
            </p>
          )}
        </div>

        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-sm font-medium text-slate-800 dark:text-gray-200">
              {serviceLabel}
            </p>
            {app.source === 'mwl_import' && (
              <span className="shrink-0 rounded bg-indigo-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300">
                MWL
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate font-mono text-[11px] text-slate-400">
            {app.accession_number || '—'}
            {app.requested_procedure_id ? ` · ${app.requested_procedure_id}` : ''}
          </p>
        </div>

        <div className="min-w-0">
          <p
            className={cn(
              'truncate text-sm',
              providerName && providerName !== t('worklist.unassigned')
                ? 'text-slate-700 dark:text-gray-200'
                : 'text-amber-600 dark:text-amber-400'
            )}
          >
            {providerName || t('worklist.unassigned')}
          </p>
          {providerIsOrderingOnly ? (
            <p className="mt-0.5 truncate text-[11px] text-slate-400">
              {t('worklist.orderingProvider', { defaultValue: 'Ordering' })}
            </p>
          ) : null}
        </div>

        <div className="min-w-0">
          <p className="truncate text-sm text-slate-700 dark:text-gray-200">{station}</p>
          {app.scheduled_station_ae_title && app.scheduled_station_name ? (
            <p className="mt-0.5 truncate font-mono text-[11px] text-slate-400">
              {app.scheduled_station_ae_title}
            </p>
          ) : app.modality && app.scheduled_station_name ? (
            <p className="mt-0.5 truncate text-[11px] text-slate-400">{app.modality}</p>
          ) : null}
        </div>

        <div className="min-w-0">
          <p className="truncate text-sm text-slate-700 dark:text-gray-200">{location}</p>
        </div>

        <div className="flex flex-wrap items-center gap-1">
          <span
            className={cn(
              'rounded-md border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              status.badgeClass
            )}
          >
            {status.label}
          </span>
          {prio && (
            <span
              className={cn(
                'rounded-md px-1.5 py-0.5 text-[9px] font-bold uppercase',
                prio.className
              )}
            >
              {prio.label}
            </span>
          )}

          {(app.mwl_sync_status === 'failed' ||
            app.mwl_sync_status === 'pending') && (
            <span
              title={
                app.mwl_sync_error ||
                'This exam is not on the modality worklist yet — retrying.'
              }
              className="rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700"
            >
              {t('worklist.notOnModality', { defaultValue: 'Not on modality' })}
            </span>
          )}
        </div>

        {canWrite ? (
          <div className="flex items-center justify-end gap-1.5">
            {showResumeActions ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1 text-xs"
                  disabled={busy}
                  onClick={() => startExam(app)}
                >
                  <FiPlay size={12} /> {t('worklist.resume')}
                </Button>
                <Button
                  size="sm"
                  variant="success"
                  className="h-8 px-2"
                  disabled={busy}
                  onClick={() =>
                    actionMutation.mutate({ id: app.id, action: 'complete' })
                  }
                  aria-label={t('worklist.complete')}
                >
                  <FiCheckCircle size={13} />
                </Button>
              </>
            ) : showStartExam ? (
              <Button
                size="sm"
                variant="primary"
                className="h-8 gap-1 text-xs"
                disabled={busy}
                title={t('startExam')}
                onClick={() => startExam(app)}
              >
                <FiPlay size={12} /> {t('startExam')}
              </Button>
            ) : null}

            {(canModify || canCancel) && (
              <div className="dui-dropdown dui-dropdown-end">
                <button
                  type="button"
                  tabIndex={0}
                  aria-label={t('actions.more')}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-700"
                >
                  <FiMoreVertical size={16} />
                </button>
                <ul
                  tabIndex={0}
                  className="dui-dropdown-content dui-menu z-30 mt-1 w-44 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl dark:border-gray-700 dark:bg-gray-800"
                >
                  {onSelectAppointment && canModify && (
                    <li>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => onSelectAppointment(app)}
                        className="text-xs font-medium"
                      >
                        {t('worklist.modify')}
                      </button>
                    </li>
                  )}
                  {canCancel && (
                    <li>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => confirmCancel(app.id)}
                        className="flex items-center gap-2 text-xs font-medium text-red-500"
                      >
                        <FiTrash size={12} /> {t('cancel')}
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div />
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden bg-card-color-100 dark:bg-gray-800',
        embedded
          ? 'rounded-2xl border border-gray-200 shadow-sm dark:border-gray-700'
          : 'rounded-xl border border-gray-200 shadow-sm dark:border-gray-700'
      )}
    >
      <div className="flex flex-col gap-4 border-b border-gray-200 p-4 dark:border-gray-700 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {!embedded && (
            <div>
              <div className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-gray-100">
              <FiList size={16} className="text-blue-600" aria-hidden />
              {t('worklist.tab')}
              </div>
              <p className="mt-1 text-xs text-slate-400">
                {dayjs(date).format('dddd, D MMMM YYYY')} · {rows.length}{' '}
                {t('worklist.items')}
                {pendingMwlRows.length > 0
                  ? ` · ${pendingMwlRows.length} MWL`
                  : ''}
              </p>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {canWrite && (
              <>
                <div className="relative">
                  <select
                    value={mwlServerId ?? ''}
                    onChange={(e) =>
                      setMwlServerId(
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    className="h-8 min-w-[160px] max-w-[240px] cursor-pointer appearance-none rounded-lg border border-gray-200 bg-white py-1.5 pl-2.5 pr-8 text-xs font-medium text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                    aria-label={t('worklist.mwlServer', {
                      defaultValue: 'MWL server',
                    })}
                    title={
                      servers.find((s: any) => Number(s.id) === mwlServerId)
                        ? mwlServerLabel(
                            servers.find(
                              (s: any) => Number(s.id) === mwlServerId
                            )
                          )
                        : undefined
                    }
                  >
                    <option value="">
                      {t('worklist.selectServer', {
                        defaultValue: 'Select MWL server',
                      })}
                    </option>
                    {usableServers.map((s: any) => (
                      <option key={s.id} value={s.id}>
                        {mwlServerLabel(s)}
                      </option>
                    ))}
                    {serversFetched && usableServers.length === 0 && (
                      <option value="" disabled>
                        {t('worklist.noUsableServer', {
                          defaultValue: 'No configured MWL server',
                        })}
                      </option>
                    )}
                  </select>
                  <FiChevronDown
                    size={14}
                    className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                    aria-hidden
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="gap-1"
                  disabled={!mwlServerId || mwlLoading || importMutation.isPending}
                  onClick={() => {
                    setDismissedMwlKeys(new Set());
                    void refetchMwl();
                  }}
                >
                  <FiRefreshCw size={14} className={mwlLoading ? 'animate-spin' : ''} />
                  <span className="hidden sm:inline">
                    {t('worklist.refreshMwl', { defaultValue: 'Refresh MWL' })}
                  </span>
                </Button>
                {Object.keys(selectedPending).length > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="gap-1"
                    disabled={importMutation.isPending}
                    onClick={() =>
                      runImport(Object.values(selectedPending))
                    }
                  >
                    {importMutation.isPending &&
                    (importMutation.variables?.keys?.length || 0) > 1 ? (
                      <FiLoader size={14} className="animate-spin" />
                    ) : (
                      <FiDownload size={14} />
                    )}
                    {t('worklist.createSelected', {
                      defaultValue: 'Create selected ({{count}})',
                      count: Object.keys(selectedPending).length,
                    })}
                  </Button>
                )}
                {pendingMwlRows.length > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="gap-1"
                    disabled={importMutation.isPending || !mwlServerId}
                    onClick={() => runImport(pendingMwlRows)}
                  >
                    {importMutation.isPending &&
                    importingKeys.size === pendingMwlRows.length ? (
                      <FiLoader size={14} className="animate-spin" />
                    ) : (
                      <FiDownload size={14} />
                    )}
                    {t('worklist.createAllMwl', {
                      defaultValue: 'Create all MWL ({{count}})',
                      count: pendingMwlRows.length,
                    })}
                  </Button>
                )}
              </>
            )}            {canWrite && onOpenBooking && (
              <Button variant="primary" size="sm" onClick={onOpenBooking}>
                {t('newAppointment')}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center rounded-lg border border-gray-200 bg-slate-50 p-1 dark:border-gray-600 dark:bg-gray-700">
            {[
              {
                key: 'all' as const,
                label: t('worklist.all'),
                count: rows.length + pendingMwlRows.length,
              },
              {
                key: 'scheduled' as const,
                label: t('status.scheduled'),
                count: grouped.scheduled.length,
              },
              {
                key: 'mwl' as const,
                label: t('worklist.mwlPending', { defaultValue: 'MWL' }),
                count: pendingMwlRows.length,
              },
              {
                key: 'in_progress' as const,
                label: t('status.inProgress'),
                count: grouped.inProgress.length,
              },
              {
                key: 'finished' as const,
                label: t('status.finished'),
                count: grouped.finished.length,
              },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setQueueFilter(item.key)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                  queueFilter === item.key
                    ? 'bg-white text-blue-700 shadow-sm dark:bg-gray-600 dark:text-blue-300'
                    : 'text-slate-500 hover:text-slate-800 dark:text-gray-300'
                )}
              >
                {item.label}
                <span className="ml-1.5 text-[10px] opacity-60">{item.count}</span>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              aria-label={t('worklist.scope', { defaultValue: 'Worklist scope' })}
              value={mode}
              onChange={setWorklistMode}
              options={[
                { value: 'facility', label: t('worklist.facility') },
                { value: 'mine', label: t('worklist.mine') },
              ]}
            />
            <label className="relative min-w-[220px] flex-1 md:max-w-sm">
              <FiSearch
                size={15}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('worklist.search')}
                aria-label={t('worklist.search')}
                className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="custom-scrollbar flex-1 overflow-auto">
        <div
          className={cn(
            WORKLIST_GRID,
            'sticky top-0 z-20 border-b border-gray-200 bg-slate-50 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:border-gray-700 dark:bg-gray-900'
          )}
        >
          <div className="flex justify-center">
            {showPendingMwl && pendingMwlRows.length > 0 ? (
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                checked={allPendingSelected}
                onChange={toggleSelectAllPending}
                aria-label={t('worklist.selectAllMwl', {
                  defaultValue: 'Select all MWL rows',
                })}
              />
            ) : null}
          </div>
          <span>{t('worklist.columns.time', { defaultValue: 'Time' })}</span>
          <span>{t('worklist.columns.patient', { defaultValue: 'Patient' })}</span>
          <span>{t('worklist.columns.procedure', { defaultValue: 'Procedure' })}</span>
          <span>{t('worklist.columns.provider', { defaultValue: 'Provider' })}</span>
          <span>{t('worklist.columns.station', { defaultValue: 'Station' })}</span>
          <span>{t('worklist.columns.location', { defaultValue: 'Location' })}</span>
          <span>{t('worklist.columns.status', { defaultValue: 'Status' })}</span>
          <span className="text-right">
            {t('worklist.columns.actions', { defaultValue: 'Actions' })}
          </span>
        </div>
        {(isLoading || (mwlLoading && !importMutation.isPending)) &&
          visibleRows.length === 0 &&
          pendingMwlRows.length === 0 && (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 px-6 py-16">
              <FiLoader
                size={22}
                className="animate-spin text-blue-500/80 dark:text-blue-400/80"
                aria-hidden
              />
              <p className="text-sm font-medium text-slate-500 dark:text-gray-400">
                {t('syncingAgenda', { defaultValue: 'Syncing agenda…' })}
              </p>
            </div>
          )}
        {!isLoading &&
          !(mwlLoading && !importMutation.isPending) &&
          visibleRows.length === 0 &&
          !(showPendingMwl && pendingMwlRows.length > 0) && (
            <div className="flex min-h-[220px] flex-col items-center justify-center gap-2 px-6 py-16">
              <FiList
                size={22}
                className="text-slate-300 dark:text-gray-600"
                aria-hidden
              />
              <p className="text-sm font-medium text-slate-500 dark:text-gray-400">
                {t('worklist.empty', {
                  defaultValue: 'No appointments for this day',
                })}
              </p>
              {mwlServerId && otherFacilityMwlCount === 0 ? (
                <p className="max-w-sm text-center text-xs text-slate-400 dark:text-gray-500">
                  {t('worklist.emptyHint', {
                    defaultValue:
                      'Local queue is empty. Pending MWL rows will appear here when available.',
                  })}
                </p>
              ) : null}

              {otherFacilityMwlCount > 0 && !includeOtherFacilities && (
                <div className="flex max-w-md flex-col items-center gap-2">
                  <p className="text-center text-xs text-slate-500 dark:text-gray-400">
                    {t('worklist.otherFacilityHidden', {
                      count: otherFacilityMwlCount,
                      defaultValue:
                        '{{count}} exam(s) came back from this worklist server but belong to another facility, so they are hidden.',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={() => setIncludeOtherFacilities(true)}
                    className="rounded-md border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
                  >
                    {t('worklist.showOtherFacility', {
                      defaultValue: 'Show them anyway',
                    })}
                  </button>
                </div>
              )}
            </div>
          )}
        {showPendingMwl && pendingMwlRows.length > 0 && (
          <>
            <div className="flex items-center justify-between gap-3 border-b border-amber-100 bg-amber-50/90 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:border-amber-900/40 dark:bg-amber-950/60 dark:text-amber-300">
              <span>
                {t('worklist.pendingMwlSection', {
                  defaultValue: 'Pending MWL · not in database yet',
                })}
                {includeOtherFacilities && otherFacilityMwlCount > 0 && (
                  <>
                    {' · '}
                    {t('worklist.includingOtherFacility', {
                      defaultValue: 'including other facilities',
                    })}
                  </>
                )}
                <span className="ml-2 font-semibold opacity-70">
                  {pendingMwlRows.length}
                </span>
              </span>
              {includeOtherFacilities && (
                <button
                  type="button"
                  onClick={() => setIncludeOtherFacilities(false)}
                  className="normal-case underline hover:no-underline"
                >
                  {t('worklist.hideOtherFacility', {
                    defaultValue: 'Only this facility',
                  })}
                </button>
              )}
            </div>
            {pendingMwlRows.map(renderPendingRow)}
          </>
        )}
        {!isLoading && queueFilter !== 'mwl' && visibleRows.length > 0 && (
          <>
            {showPendingMwl && pendingMwlRows.length > 0 && (
              <div className="border-b border-slate-200 bg-slate-50/95 px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:border-gray-700 dark:bg-gray-900/80 dark:text-gray-400">
                {t('worklist.localSection', {
                  defaultValue: 'In schedule',
                })}
                <span className="ml-2 font-semibold opacity-70">
                  {visibleRows.length}
                </span>
              </div>
            )}
            {visibleRows.map(renderRow)}
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={!!cancelTargetId}
        onClose={() => setCancelTargetId(null)}
        onConfirm={handleConfirmCancel}
        title={t('messages.cancelConfirmTitle', {
          defaultValue: 'Cancel this appointment?',
        })}
        content={t('messages.cancelConfirmBody', {
          defaultValue: 'This cannot be undone.',
        })}
        confirmText={t('cancel')}
        cancelText={t('modal.discard')}
        isDestructive
      />
      {examFlowModals}
    </div>
  );
}
